import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/authContext.jsx";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator.js";
import {
  handlefirstNameValueChange,
  handleLastNameChange,
  handleEmailChange,
} from "../../controller/watcherAndClearFunctions/signupSettingsClearsWatchersFunctions.js";
import { signupRemainingFieldsErrorHandler } from "../../controller/errorHandlers/signupRemainingFieldsErrorHandler.js";
import { PaperPlaneIcon, PaperPenWritingIcon } from "../icons/index.jsx";
import {
  InsetButtonEventListener,
  ImgTopBtn,
  InsetBlendTVButton,
  FieldButton,
} from "../buttons/index.jsx";
import BlinkingLight from "../blinkingLight/blinkingLight.jsx";
import Loader from "../loader/loader.jsx";
import { apiAddAcctAtributes, apiCheckEmail } from "../../api/backend/auth.js";
import { uploadFileToS3 } from "../../api/backend/uploadToS3.js";
import {
  getPresignedImgUrl,
  apiRecordProfileImageUrlCrud,
} from "../../api/backend/user.js";
import { getImageFileType, resizeFile } from "../../constants/fileUpload";
import { checkFile } from "../../utils/checkFile";
import { SearchGlassIcon } from "../icons/index.jsx";
import { errorMessages } from "../../constants/errorMessages.js";
import ErrorMessageModal from "../errorMessageModal/errorMessageModal.jsx";
import { emoteOptions } from "../../constants/emoteOptions";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes.js";
import EmotionEmoji from "../emoji/emoji.jsx";
import "./buildProfileSignupForm.scss";

const BuildProfileSignupForm = ({
  onCompleteProfile,
  onCancel,
  currentContextType,
  currentMemberData,
  forcePhoto,
  userId,
}) => {
  const { setCurrentMemberData, currentMemberId } = useAuth();
  const [loading, setLoading] = useState(false);
  // Fetch values from local storage
  // Build a list of the *field names* you want to hydrate from localStorage:
  const fieldNames = [
    "firstNameValue",
    "lastNameValue",
    "emailValue",
    "thumbnailPreference",
    "currentEmotionId",
    "profileImagePreviewUrl",
  ];

  // Then turn that into real storage‑keys using your getStorageKey util:
  const localStorageKeys = fieldNames.map((name) =>
    getStorageKey(name, currentContextType)
  );

  // And finally pull them all into an object:
  const localStorageValues = localStorageKeys.reduce((acc, key, i) => {
    // keep the original fieldName as the map key
    acc[fieldNames[i]] = localStorage.getItem(key);
    return acc;
  }, {});

  const [firstNameValue, setFirstNameValue] = useState(() => {
    if (currentMemberData.firstName) return currentMemberData.firstName;
    return localStorageValues.firstNameValue || "";
  });
  const [lastNameValue, setLastNameValue] = useState(() => {
    if (currentMemberData.lastName) return currentMemberData.lastName;
    return localStorageValues.lastNameValue || "";
  });
  const [emailValue, setEmailValue] = useState(() => {
    if (currentMemberData.email) return currentMemberData.email;
    return localStorageValues.emailValue || "";
  });
  const [thumbnailPreference, setThumbnailPreference] = useState(() => {
    if (forcePhoto) return "picture"; // passed in from the parent, data validation, must be on the signup only
    if (currentMemberData.profileImageUrl) return "picture";
    return localStorageValues.thumbnailPreference || "emoji";
  });
  const [imagePreviewUrl, setImagePreviewUrl] = useState(() => {
    if (currentMemberData.profileImageUrl)
      return currentMemberData.profileImageUrl;
    return localStorageValues.profileImagePreviewUrl || "";
  });

  const [firstNameValueError, setFirstNameValueError] = useState("");
  const [lastNameValueError, setLastNameValueError] = useState("");
  const [emailValueError, setEmailValueError] = useState("");
  const [firstNameFocused, setFirstNameFocused] = useState(false);
  const [lastNameFocused, setLastNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const [thumbnailError, setThumbnailError] = useState("");
  const [emailResponseError, setEmailResponseError] = useState("");
  const [fileUpload, setFileUpload] = useState(null);
  const [resizeError, setResizeError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [presignedImgUrlResponse, setPresignedImgUrlResponse] = useState(null);
  const [previewImgModal, setPreviewImgModal] = useState(false);
  const [imageMetaData, setImageMetaData] = useState({
    width: 0,
    height: 0,
    size: 0, // Add this to track the file size
  });
  const fileInputRef = useRef(null);

  const actionWord =
    currentContextType === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM
      ? "Reset Password"
      : "Login";

  const dynamicButtonText = `Complete Profile & ${actionWord}`;

  useEffect(() => {
    localStorage.setItem(
      getStorageKey("firstNameValue", currentContextType),
      firstNameValue
    );
    localStorage.setItem(
      getStorageKey("lastNameValue", currentContextType),
      lastNameValue
    );
    localStorage.setItem(
      getStorageKey("emailValue", currentContextType),
      emailValue
    );
    localStorage.setItem(
      getStorageKey("thumbnailPreference", currentContextType),
      thumbnailPreference
    );
  }, [firstNameValue, lastNameValue, emailValue, thumbnailPreference]);
  const checkEmail = useCallback(async (email) => {
    setEmailValueError(""); // clear previous errors
    try {
      const { exists } = await apiCheckEmail(email);
      if (typeof exists !== "boolean") {
        setEmailValueError("Unexpected response. Please try again later.");
        return false;
      }
      return exists;
    } catch {
      setEmailValueError("Error checking email. Try again.");
      return false;
    }
  }, []);

  const triggerFileInputClick = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  useEffect(() => {
    // 1) Persist the forced thumbnailPreference
    localStorage.setItem(
      getStorageKey("thumbnailPreference", currentContextType),
      thumbnailPreference
    );

    // 2) Always clear any old emoji ID
    localStorage.removeItem(
      getStorageKey("currentEmotionId", currentContextType)
    );

    // 3) Sync the image preview URL
    if (imagePreviewUrl) {
      // so if they reload, we show their preview
      localStorage.setItem(
        getStorageKey("profileImagePreviewUrl", currentContextType),
        imagePreviewUrl
      );
    } else {
      localStorage.removeItem(
        getStorageKey("profileImagePreviewUrl", currentContextType)
      );
    }
  }, [thumbnailPreference, imagePreviewUrl, currentContextType]);

  const prepareImagePreviewAndShowModal = (file) => {
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl); // Set state to display the preview
  };

  const handleUploadImage = async (e) => {
    console.log("handleUploadImage: event triggered");
    const file = e?.target?.files[0];
    if (!file) {
      console.log("No file selected");
      return;
    }
    const fileType = getImageFileType(file); // Determines if the file is 'jpeg', 'png',
    console.log("Selected file:", file);
    console.log("File MIME type:", file.type);
    console.log("File size:", file.size);
    try {
      checkFile(file.name, file.type, file.size); // Perform file validation and throw error if not valid
      console.log("File validation passed, no resizing needed.");
      setThumbnailPreference("picture");
      setImageMetaData((prevMetadata) => ({
        ...prevMetadata,
        size: file.size,
      }));
      setFileUpload(file);
      prepareImagePreviewAndShowModal(file);
      setResizeError(false);
    } catch (resizeError) {
      console.error("Validation error:", resizeError.message);
      if (file.size > 250000) {
        console.log("Attempting to resize file due to exceeding size limit...");
        try {
          const resizedFile = await resizeFile(file, fileType);
          console.log("Resized file:", resizedFile);
          setImageMetaData((prevMetadata) => ({
            ...prevMetadata,
            size: resizedFile.size,
          }));
          setFileUpload(resizedFile);
          prepareImagePreviewAndShowModal(resizedFile);
        } catch (resizeError) {
          setResizeError(resizeError); // Use the caught error from resizing attempt
          console.error("Error resizing file:", resizeError);
        }
      } else {
        console.error("Error during file handling:", resizeError);
      }
    }
  };

  const handleSubmitUpload = async () => {
    if (!fileUpload) {
      console.error("File validation failed");
      setFileError(true);
      setThumbnailError(errorMessages.profileSignupPic);
      return;
    }
    const formData = {
      fileName: fileUpload.name,
      mimeType: fileUpload.type,
      fileSize: fileUpload.size,
      sourceId: userId,
    };
    console.log("Form data constructed:", formData);
    localStorage.setItem("formData", JSON.stringify(formData));
    setLoading(true);
    try {
      const presignedImgUrlResponse = await getPresignedImgUrl(
        fileUpload.name,
        fileUpload.type,
        fileUpload.size,
        formData.sourceId
      );
      if (presignedImgUrlResponse.presignedImgUrl) {
        console.log(
          "Pre-signed URL obtained:",
          presignedImgUrlResponse.presignedImgUrl
        );
        setPresignedImgUrlResponse(presignedImgUrlResponse);
      } else {
        console.error("Failed to obtain pre-signed URL");
      }
    } catch (err) {
      console.error("Error during the upload setup process:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("useEffect triggered", { presignedImgUrlResponse, fileUpload });
    if (presignedImgUrlResponse?.presignedImgUrl && fileUpload) {
      // this response will invoke the following below
      console.log(
        "Attempting upload with URL:",
        presignedImgUrlResponse.presignedImgUrl
      );
      console.log("Uploading file details:", {
        URL: presignedImgUrlResponse.presignedImgUrl,
        "File MIME type": fileUpload.type,
        File: fileUpload,
      });
      // using the file object directly
      uploadFileToS3(
        presignedImgUrlResponse.presignedImgUrl,
        fileUpload, // this is the actual file object that needs to be uploaded
        handleUploadSuccess, // Direct reference, not invoking here instead of a successs response message commented out below
        (error) => console.error("Upload Error:", error)
      );
    }
  }, [presignedImgUrlResponse, fileUpload]);

  const handleUploadSuccess = async () => {
    const formDataString = localStorage.getItem("formData");
    const sourceId = userId;
    const cloudFrontDomain = "media.skitsicon.com"; // Define CloudFront domain
    if (formDataString) {
      let userData = JSON.parse(formDataString);
      userData.profileImageUrl = `https://${cloudFrontDomain}/images/${sourceId}/${encodeURIComponent(userData.fileName)}`;
      localStorage.setItem("profileImageUrl", userData.profileImageUrl);
      delete userData.imageMetaData; // Assuming you want to remove this before sending
      console.log("Constructed CloudFront URL:", userData.profileImageUrl);
      try {
        const response = await apiRecordProfileImageUrlCrud(userData);
        if (response.status === 200 || response.data.success) {
          console.log("Profile URL data recorded successfully:", response.data);
          localStorage.removeItem("formData");
          setLoading(false);
          setFileUpload(null);
          handleModalClose();
          finalizeProfileCompletion(); // Call without parameters
        } else {
          console.error("Failed to record user data:", response.data);
        }
      } catch (error) {
        console.error("Error recording user data:", error);
      }
    } else {
      console.error("No profile data found in local storage.");
    }
  };

  const handleModalClose = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setPreviewImgModal(false);
  };
  const finalizeProfileCompletion = async () => {
    try {
      const payload = {
        userId,
        firstName: firstNameValue,
        lastName: lastNameValue,
        email: emailValue,
        thumbnailPreference,
      };
      if (thumbnailPreference === "emoji") {
        const emotionconId = localStorage.getItem(
          getStorageKey("currentEmotionId", currentContextType)
        );
        if (emotionconId !== "0") {
          payload.emotionconId = emotionconId;
        }
      }
      const referral = localStorage.getItem(
        getStorageKey("usernameReferral", currentContextType)
      );
      if (referral) {
        payload.referral = referral;
      }
      console.log("[BuildProfile] Sending attributes payload:", payload);
      const result = await apiAddAcctAtributes(payload);
      console.log("[BuildProfile] apiAddAcctAtributes result:", result.data);
        if (result.data?.status === "OK" && result.data?.user) {
           let updatedUser = result.data.user;
            if (referral && !updatedUser.referral) {
              updatedUser = { ...updatedUser, referral };
            }
        setCurrentMemberData(updatedUser);
        console.log("[BuildProfile] Calling onCompleteProfile callback");
        // call back to redirect and update the authorization form (code flow and form control), passing data to set state in auto login (centralizing data setter)
        // now include currentContextType as second arg:
         // Pass the full user to the parent (matches your onCompleteProfile(updatedUser, ctx) shape)
        onCompleteProfile(updatedUser, currentContextType);
        localStorage.removeItem(
          getStorageKey("firstNameValue", currentContextType)
        );
        localStorage.removeItem(
          getStorageKey("lastNameValue", currentContextType)
        );
        localStorage.removeItem(
          getStorageKey("emailValue", currentContextType)
        );
        localStorage.removeItem(
          getStorageKey("thumbnailPreference", currentContextType)
        );
        localStorage.removeItem(
          getStorageKey("currentEmotionId", currentContextType)
        );
        localStorage.removeItem(
          getStorageKey("usernameReferral", currentContextType)
        );
        console.log("[BuildProfile] Cleared localStorage for signup keys");
      } else {
        console.warn(
          "[BuildProfile] apiAddAcctAtributes returned non-OK:",
          result.data
        );

        setEmailResponseError(
          result.data.message || "An error occurred during profile completion."
        );
      }
      setLoading(false);
    } catch (error) {
      console.error("Profile completion error:", error);
      if (error.response?.status === 409) {
        setEmailResponseError("Account already registered with this email");
      } else {
        setErrors(
          error.response?.data?.message ||
            "An error occurred during profile completion."
        );
      }
      setLoading(false);
    }
  };

  const onProfileComplete = async (e) => {
    e.preventDefault();
    setLoading(true);
    // 1) Validate input fields and file upload
    const errors = signupRemainingFieldsErrorHandler(
      currentMemberData,
      currentContextType,
      setFirstNameValueError,
      setLastNameValueError,
      setEmailValueError,
      setThumbnailError,
      forcePhoto,
      fileUpload
    );
    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }
    // 2) Check if email is already in use
    const emailRes = await checkEmail(emailValue);
    if (emailRes && emailRes.exists) {
      setEmailValueError("Account already registered with this email");
      setLoading(false);
      return;
    }
    // 3) If profile picture upload is required
    // 3) Picture logic:
    if (thumbnailPreference === "picture") {
      if (fileUpload) {
        // user selected a new file → upload it
        await handleSubmitUpload();
        // handleSubmitUpload will call finalizeProfileCompletion() on success
        return;
      } else if (imagePreviewUrl) {
        // no new file but we already have a backend photo → skip upload
        finalizeProfileCompletion();
        return;
      } else {
        // truly missing a picture
        setLoading(false);
        setThumbnailError(errorMessages.profileSignupPic);
        return;
      }
    }
    // 4) Final fallback (emoji or otherwise)
    finalizeProfileCompletion();
  };
  const handleCancel = (e) => {
    // hand your setLoading into the parent handler
    onCancel(e, setLoading);
  };

  return (
    <div
      id="buildProfileForm"
      className="complete-profile-modal-content slideDown"
    >
      <div className="complete-profile-modal-body">
        <form action="" style={{ position: "relative" }}>
          <div>
            <div id="logo-header">
              <h3>Complete Your Profile</h3>
            </div>
            <div className="input-field">
              <div className="input-group blinker-wrapper">
                {emailResponseError && (
                  <div className="email-signup-error-message">
                    {emailResponseError}
                  </div>
                )}
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="userEmail"
                    autoComplete="off"
                    placeholder={
                      emailValueError
                        ? emailValueError
                        : "Enter your email here"
                    }
                    className={`${emailFocused ? "focused" : ""} ${
                      emailValueError ? "field-background-color-error" : ""
                    }`}
                    value={emailValue}
                    onChange={(e) =>
                      handleEmailChange(
                        e.target.value,
                        setEmailValue,
                        currentContextType,
                        getStorageKey,
                        emailValueError,
                        setEmailValueError
                      )
                    }
                    onFocus={() => {
                      setEmailFocused(true);
                      setEmailValueError(""); // Clear error on focus
                    }}
                    onBlur={() => setEmailFocused(false)}
                  />
                  {emailValue && (
                    <FieldButton onClick={() => setEmailValue("")}>
                      Clear
                    </FieldButton>
                  )}
                </div>
                {!(emailFocused || emailValue) && (
                  <>
                    <PaperPlaneIcon
                      className="input-icon"
                      fillColor="var(--color-white)"
                      style={{
                        opacity: 0.68,
                        transition: "opacity 0.2s ease-in-out",
                      }}
                    />
                    <span className="blinking-cursor-black-color signup-email-cursor">
                      |
                    </span>
                  </>
                )}
              </div>
              {emailValueError && emailValueError !== errorMessages.email && (
                <div className="build-profile-error-message">
                  {emailValueError}
                </div>
              )}
              <div className="input-label"></div>
              <div className="input-grid2 blinker-wrapper">
                <section className="full-name-section">
                  <div className="input-group blinker-wrapper">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="firstName"
                        autoComplete="given-name"
                        placeholder={
                          firstNameValueError
                            ? firstNameValueError
                            : "Enter Your Legal First Name Here"
                        }
                        className={`${firstNameFocused ? "focused" : ""} ${
                          firstNameValueError
                            ? "field-background-color-error"
                            : ""
                        }`}
                        value={firstNameValue}
                        onChange={(e) =>
                          handlefirstNameValueChange(
                            e.target.value,
                            currentContextType,
                            setFirstNameValue,
                            firstNameValueError,
                            setFirstNameValueError
                          )
                        }
                        onFocus={() => setFirstNameFocused(true)}
                        onBlur={() => setFirstNameFocused(false)}
                      />
                      {firstNameValue && (
                        <FieldButton onClick={() => setFirstNameValue("")}>
                          Clear
                        </FieldButton>
                      )}
                      {!(firstNameFocused || firstNameValue) && (
                        <>
                          <PaperPenWritingIcon
                            className="input-icon"
                            fillColor="var(--color-white)"
                            style={{
                              opacity: 0.68,
                              transition: "opacity 0.2s ease-in-out",
                            }}
                          />
                          <span className="blinking-cursor-black-color signup-firstname-cursor">
                            |
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="input-group blinker-wrapper">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="lastName"
                        autoComplete="family-name"
                        placeholder={
                          lastNameValueError
                            ? lastNameValueError
                            : "Enter Your Legal Last Name Here"
                        }
                        className={`${lastNameFocused ? "focused" : ""} ${
                          lastNameValueError
                            ? "field-background-color-error"
                            : ""
                        }`}
                        value={lastNameValue}
                        onChange={(e) =>
                          handleLastNameChange(
                            e.target.value,
                            lastNameValueError,
                            currentContextType,
                            setLastNameValue,
                            getStorageKey,
                            setLastNameValueError
                          )
                        }
                        onFocus={() => setLastNameFocused(true)}
                        onBlur={() => setLastNameFocused(false)}
                      />
                      {lastNameValue && (
                        <FieldButton onClick={() => setLastNameValue("")}>
                          Clear
                        </FieldButton>
                      )}
                      {!(lastNameFocused || lastNameValue) && (
                        <>
                          <PaperPenWritingIcon
                            className="input-icon"
                            fillColor="var(--color-white)"
                            style={{
                              opacity: 0.68,
                              transition: "opacity 0.2s ease-in-out",
                            }}
                          />
                          <span className="blinking-cursor-black-color signup-lastname-cursor">
                            |
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </section>
              </div>
              <div className="profile-pic-upload-section">
                <div className="decorative-emojis" aria-hidden="true">
                  {emoteOptions.map((opt) => (
                    <EmotionEmoji
                      key={opt.id}
                      currentEmotionId={opt.id}
                      showAllEmojis={false} // forces “defaultOrSelected” display only
                      // no onSelectEmoji prop → clicks do nothing
                    />
                  ))}
                </div>
                <label className="upload-preamble">
                  People love a little personality—show yours with a smiley
                  face!
                </label>
                <InsetBlendTVButton
                  image={<SearchGlassIcon />}
                  btnText="Choose and upload a profile pic"
                  textStyles={{
                    color: "var(--color-bloodred)",
                    fontFamily: "Roboto Slab",
                    fontWeight: "800",
                    fontSize: "1rem",
                  }}
                  onClick={triggerFileInputClick}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/jpeg,image/png"
                  onChange={handleUploadImage}
                />
                {imagePreviewUrl && (
                  <div className="image-preview-container">
                    <img
                      src={imagePreviewUrl}
                      alt="Profile Preview"
                      style={{ maxWidth: "200px", maxHeight: "200px" }}
                    />
                  </div>
                )}
                {thumbnailError && (
                  <ErrorMessageModal
                    message={thumbnailError}
                    onClose={() => setThumbnailError("")}
                  />
                )}
              </div>
            </div>
            <div className="position-relative">
              <ImgTopBtn
                image={
                  <BlinkingLight blinkerLightStyles={{ marginRight: "10px" }} />
                }
                imgTopBtnWidth="100%"
                imgTopBtnFontColor="#b03a2e"
                imgTopBtnText={dynamicButtonText}
                textClassName="responsiveBtnText"
                imgTopBtnTextFontSize="1.3rem"
                onClick={onProfileComplete}
              />
            </div>
            {loading && <Loader className="complete-profile-loader-position" />}
            <InsetButtonEventListener
              btnText="X Cancel"
              className="white-gold"
              textClassName="responsiveBtnText"
              style={{
                width: "150px",
                fontSize: "1.3rem",
                fontFamily: "roboto-slab",
                fontWeight: "600",
              }}
              onClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default BuildProfileSignupForm;
